'use client'

import { load, trackPageview } from 'fathom-client'
import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { DEAL_SIGNUP_VALUE_CENTS, trackFormSignup } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'

const logger = createLogger('FathomAnalytics')

const SIGNUP_COOKIE = 'fathom-track-signup'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

export default function FathomAnalytics() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const siteId = process.env.NEXT_PUBLIC_FATHOM_SITE_ID

  useEffect(() => {
    if (!siteId) return
    load(siteId, { auto: false })
  }, [siteId])

  // Only send the external referrer on the first pageview. document.referrer
  // persists for the lifetime of the page — it always reflects the *initial*
  // navigation, not subsequent client-side route changes. Without this guard
  // every SPA navigation re-attributes the pageview to the original external
  // referrer (e.g. Google), inflating "from Google" counts in Fathom.
  const hasTrackedReferrer = useRef(false)

  useEffect(() => {
    if (!siteId) return
    if (!pathname) return

    // Skip tracking for cache warmup requests (EA-641)
    if (searchParams?.get('ref') === 'cache-warmup') return

    const qs = searchParams?.toString()

    // Filter out same-origin referrers so internal navigations (full-page
    // loads from `window.location.assign`, redirects, new tabs) don't
    // pollute Fathom's referrer data with efficient.app → efficient.app
    // noise (EA-647).
    //
    // IMPORTANT: per fathom-client docs, when the `referrer` key is OMITTED,
    // Fathom falls back to reading `document.referrer` itself. Omitting the
    // key is NOT the same as "no referrer" — it's "use the browser's
    // referrer". So for same-origin hits we must pass `referrer: ''`
    // explicitly to override that fallback. (An earlier version of this
    // code omitted the key and the bug persisted until 2026-04-10.)
    let externalReferrer = ''
    if (!hasTrackedReferrer.current && document.referrer) {
      try {
        const refHost = new URL(document.referrer).hostname
        const isSameOrigin =
          refHost === window.location.hostname ||
          refHost === 'efficient.app' ||
          refHost.endsWith('.efficient.app')
        if (!isSameOrigin) {
          externalReferrer = document.referrer
        }
      } catch {
        // malformed referrer — leave as empty string so Fathom gets no referrer
      }
    }

    trackPageview({
      url: qs ? `${pathname}?${qs}` : pathname,
      referrer: externalReferrer,
    })
    hasTrackedReferrer.current = true
  }, [pathname, searchParams, siteId])

  // Fire deferred signup event for Google OAuth (cookie set server-side in auth.config.ts)
  useEffect(() => {
    if (!siteId) return
    const signupCookie = readCookie(SIGNUP_COOKIE)
    if (signupCookie === null) return
    // EA-821: 'deal' marks a deal-flow signup, tracked at a lower value to avoid
    // double-counting the deal claim. Any other value is a standard signup.
    const valueCents = signupCookie === 'deal' ? DEAL_SIGNUP_VALUE_CENTS : undefined
    // Fathom script may not be loaded yet on initial mount — retry briefly
    function tryTrack() {
      if (window.fathom) {
        trackFormSignup(valueCents)
        document.cookie = `${SIGNUP_COOKIE}=; path=/; max-age=0`
        return true
      }
      return false
    }

    if (tryTrack()) return
    let attempts = 0
    const interval = setInterval(() => {
      if (tryTrack()) {
        clearInterval(interval)
      } else if (++attempts >= 10) {
        clearInterval(interval)
        logger.error('Failed to track "Form: Signup" event: Fathom script did not load within 1s')
      }
    }, 100)
    return () => clearInterval(interval)
  }, [pathname, siteId])

  return null
}
